/**
 * Sprint 11 Phase 2 — Individual client coaching view.
 *
 * Replaces the heatmap/cohort surface for individual deployments.
 * Renders the assigned coach's single client as a radar of the 7
 * dimensions + per-dimension scorecards (mean, rubric band, verbatim
 * spec interpretation sentence).
 *
 * Pre-completion the endpoint returns `status: awaiting_response`;
 * we show a friendly waiting-room panel rather than empty cells.
 */
import React from "react";
import { api, formatApiError } from "../lib/api";
import CoachingReportPanel from "./CoachingReportPanel";
import NudgeDot from "./NudgeDot";
import ItemReadingPanel from "./ItemReadingPanel";
import CoachingPlanPanel from "./CoachingPlanPanel";
import ResourceSummary from "./ResourceSummary";

// Sprint 11 Phase 3a — relative time helper for the engagement-loop
// indicator. Kept local rather than imported from SendLinkButton so
// the two components don't accidentally couple as I move them around.
function relativeFromNow(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  const diffMs = Date.now() - then;
  if (diffMs < 60000) return "just now";
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return days === 1 ? "yesterday" : `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  return new Date(iso).toLocaleDateString();
}

const DIM_ORDER = [
  "optimism", "emotional_regulation", "ownership", "flexibility",
  "social_support", "purpose", "grit",
];

const RUBRIC_COLOR = {
  "Strong": "#1B8A6B",
  "Developing": "#2F6B7C",
  "Emerging": "#D78E2C",
  "At Risk": "#AD393E",
};

// Short axis labels for the radar — the full DIMENSION labels
// ("OWNERSHIP & INITIATIVE" / "PURPOSE & MISSION" / "GRIT &
// PERSEVERANCE") overflow the 400px column at any reasonable chart
// size. Radar charts conventionally use terse axis text; the
// scorecards underneath still render the full label.
const RADAR_SHORT_LABEL = {
  optimism: "OPTIMISM",
  emotional_regulation: "EMOTIONAL REG.",
  ownership: "OWNERSHIP",
  flexibility: "FLEXIBILITY",
  social_support: "SOCIAL SUPPORT",
  purpose: "PURPOSE",
  grit: "GRIT",
};

// 7-axis radar at fixed canvas dims. Each dimension's mean is plotted
// on a 1..5 scale (the scored value range). Axis 0 sits at the top.
function Radar({ dimensions, size = 360 }) {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 36;
  const n = dimensions.length;
  const minVal = 1, maxVal = 5;

  function point(i, value) {
    const ratio = Math.max(0, Math.min(1, (value - minVal) / (maxVal - minVal)));
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    return [cx + Math.cos(angle) * radius * ratio, cy + Math.sin(angle) * radius * ratio];
  }

  function ring(ratio) {
    const pts = [];
    for (let i = 0; i < n; i++) {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      pts.push([cx + Math.cos(angle) * radius * ratio, cy + Math.sin(angle) * radius * ratio]);
    }
    return pts.map((p) => p.join(",")).join(" ");
  }

  const dataPoly = dimensions.map((d, i) => point(i, d.mean)).map((p) => p.join(",")).join(" ");

  return (
    // overflow="visible" lets the long Oswald uppercase axis labels
    // (OWNERSHIP & INITIATIVE / PURPOSE & MISSION / GRIT & PERSEVERANCE)
    // render outside the SVG's nominal viewBox without clipping. The
    // chart geometry stays exactly the same; only the label text
    // bleeds into the surrounding container, which has the padding
    // budget to absorb it.
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} overflow="visible" style={{ overflow: "visible" }} data-testid="individual-radar" role="img" aria-label="Resilience profile radar">
      {/* concentric reference rings at 20/40/60/80/100% of range */}
      {[0.2, 0.4, 0.6, 0.8, 1].map((r) => (
        <polygon key={r} points={ring(r)} fill="none" stroke="#E5E5E5" strokeWidth="1" />
      ))}
      {/* radial spokes */}
      {dimensions.map((d, i) => {
        const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        const x = cx + Math.cos(angle) * radius;
        const y = cy + Math.sin(angle) * radius;
        return <line key={d.dimension} x1={cx} y1={cy} x2={x} y2={y} stroke="#E5E5E5" strokeWidth="1" />;
      })}
      {/* data polygon */}
      <polygon points={dataPoly} fill="#2563EB" fillOpacity="0.15" stroke="#2563EB" strokeWidth="2" />
      {/* axis labels */}
      {dimensions.map((d, i) => {
        const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        const x = cx + Math.cos(angle) * (radius + 22);
        const y = cy + Math.sin(angle) * (radius + 22);
        const anchor = Math.abs(Math.cos(angle)) < 0.2 ? "middle"
          : Math.cos(angle) > 0 ? "start" : "end";
        return (
          <text
            key={d.dimension}
            x={x}
            y={y}
            textAnchor={anchor}
            dominantBaseline="middle"
            fontFamily="Oswald"
            fontSize="11"
            fill="#1A1A1A"
            style={{ letterSpacing: "0.05em", textTransform: "uppercase" }}
          >
            {RADAR_SHORT_LABEL[d.dimension] || d.label}
          </text>
        );
      })}
    </svg>
  );
}

function Scorecard({ dim, items }) {
  const color = RUBRIC_COLOR[dim.rubric_level] || "#555";
  // Sprint 12 — within-dimension item list. Items come from the full
  // ranking endpoint; we filter to this dimension and sort by item_number
  // (canonical order, NOT score order — Spec 5.1 says "in item_number
  // order" for the within-dimension display).
  const itemsForDim = (items || [])
    .filter((it) => it.dimension === dim.dimension)
    .sort((a, b) => a.item_number - b.item_number);

  return (
    <div
      className="p-5"
      data-testid={`scorecard-${dim.dimension}`}
      style={{ background: "white", border: "1px solid #E0E0E0", borderLeft: `4px solid ${color}` }}
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="eyebrow" style={{ color: "#2F6B7C" }}>{dim.label}</div>
        <div className="text-xs uppercase tracking-widest" style={{ color, fontFamily: "Oswald" }}>
          {dim.rubric_level}
        </div>
      </div>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="text-3xl" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}>
          {dim.mean.toFixed(2)}
        </span>
        <span className="text-xs text-gray-500">mean / 5.00</span>
      </div>
      <p className="text-sm leading-relaxed text-ink" data-testid={`scorecard-interp-${dim.dimension}`}>
        {dim.interpretation}
      </p>
      {itemsForDim.length > 0 && (
        <ul
          data-testid={`scorecard-items-${dim.dimension}`}
          className="mt-4 pt-3 space-y-1"
          style={{ listStyle: "none", padding: 0, margin: 0, borderTop: "1px solid #F0F0F0", paddingTop: "12px" }}
        >
          {itemsForDim.map((it) => (
            <li
              key={`scorecard-item-${dim.dimension}-${it.item_number}`}
              className="flex items-baseline gap-3 text-xs"
              style={{ color: "#1A1A1A", lineHeight: 1.4 }}
            >
              {/* Within-dimension display uses positive-form throughout
                  per Spec 5.1 "lean toward consistency". */}
              <span className="flex-1 min-w-0">{it.text_strength}</span>
              <span
                style={{
                  fontFamily: "Oswald",
                  fontWeight: 700,
                  color: it.scored_value >= 4 ? "#1B8A6B" : it.scored_value <= 2 ? "#D97706" : "#7F7F7F",
                  minWidth: "20px",
                  textAlign: "right",
                }}
              >
                {it.scored_value}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function IndividualClientView({ deploymentId }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  // Sprint 12 — item-level ranking, fetched once at completion so the
  // scorecards and the cross-dimensional panel render off the same data.
  const [fullRanking, setFullRanking] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get(`/api/deployments/${deploymentId}/individual-client`);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deploymentId]);

  // Fetch item ranking once we know the respondent has completed.
  // 409s pre-completion are expected — silently skipped.
  React.useEffect(() => {
    if (!data || data.status !== "completed" || !data.respondent_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: r } = await api.get(`/api/respondents/${data.respondent_id}/item-ranking`);
        if (!cancelled) setFullRanking(r.full_ranking);
      } catch (_e) {
        // Don't surface — scorecards still render without the per-item list.
      }
    })();
    return () => { cancelled = true; };
  }, [data]);

  if (loading) {
    return <div className="text-sm text-gray-500" data-testid="individual-client-loading">Loading client profile…</div>;
  }
  if (error) {
    return <div className="text-sm" style={{ color: "#AD393E" }} data-testid="individual-client-error">{error}</div>;
  }
  if (!data) return null;

  if (data.status === "awaiting_response") {
    // Sprint 11 Phase 3a — engagement-loop indicator. Derive the
    // visible state from the timestamps we already track:
    //   sent + !started  → "Link emailed N days ago, not yet opened"
    //   started + !completed → "Opened, in progress"
    //   neither sent nor started → "Generate / send the link"
    const sent = data.invitation_link_sent_at;
    const started = data.started_at;
    let state, label, color;
    if (started) {
      state = "in_progress";
      label = `Opened ${relativeFromNow(started)} · in progress`;
      color = "#D78E2C";
    } else if (sent) {
      state = "delivered_unopened";
      label = `Link emailed ${relativeFromNow(sent)} · not yet opened`;
      color = "#2563EB";
    } else {
      state = "not_sent";
      label = "Survey link has not been sent to the client yet";
      color = "#6F6F6F";
    }
    return (
      <section
        data-testid="individual-client-awaiting"
        data-state={state}
        className="p-8"
        style={{ background: "white", border: "1px solid #E0E0E0" }}
      >
        <div className="eyebrow mb-2" style={{ color: "#2563EB" }} data-testid="individual-client-eyebrow">
          Personal Resilience Profile
        </div>
        <h2 className="text-2xl mb-3" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}>
          {data.client_name}
        </h2>
        <div
          className="text-sm mb-4"
          data-testid="engagement-state-label"
          style={{ color }}
        >
          {label}
        </div>
        <p className="text-sm text-gray-700 max-w-prose mb-4">
          The 7-dimension profile, radar, and coaching scorecards will appear here as
          soon as the survey is submitted.
        </p>
        <div className="text-xs text-gray-500 space-y-1">
          {sent && (
            <div>Link emailed {new Date(sent).toLocaleString()}</div>
          )}
          {started && (
            <div>Started {new Date(started).toLocaleString()}</div>
          )}
        </div>
      </section>
    );
  }

  // Sort dims into the canonical order for both radar + scorecards.
  const dims = DIM_ORDER
    .map((k) => data.dimensions.find((d) => d.dimension === k))
    .filter(Boolean);

  return (
    <section data-testid="individual-client-completed" className="space-y-8">
      <header>
        <div className="eyebrow mb-1" style={{ color: "#2563EB" }} data-testid="individual-client-eyebrow">
          Personal Resilience Profile
        </div>
        <h2 className="text-2xl" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}>
          {data.client_name}
        </h2>
        <p className="text-xs text-gray-500 mt-1">
          Completed {new Date(data.completed_at).toLocaleString()}
          {data.duration_seconds ? ` · ${Math.round(data.duration_seconds / 60)} min` : ""}
          <NudgeDot
            reportSentAt={data?.report?.report_sent_at}
            deploymentId={deploymentId}
            respondentId={data?.respondent_id}
          />
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[400px_minmax(0,1fr)] gap-8 items-start">
        <div className="flex justify-center"><Radar dimensions={dims} /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dims.map((d) => <Scorecard key={d.dimension} dim={d} items={fullRanking} />)}
        </div>
      </div>

      <ResourceSummary resources={data.resources} meta={data.resources_meta} register="individual" />

      <ItemReadingPanel respondentId={data?.respondent_id} />

      <CoachingPlanPanel
        respondentId={data?.respondent_id}
        clientName={data.client_name}
        isCompleted={data?.status === "completed"}
        resources={data.resources}
      />

      <CoachingReportPanel
        deploymentId={deploymentId}
        clientName={data.client_name}
        report={data.report}
      />
    </section>
  );
}
